<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import dealReportsAPI from 'dashboard/api/dealReports';
import TableHeaderCell from './TableHeaderCell.vue';
import { formatCurrencyBRL } from './commercialFormatters';

const props = defineProps({
  params: { type: Object, required: true },
  // "Meu espaço": só as negociações do usuário logado (o backend força o id).
  mine: { type: Boolean, default: false },
});

const { t } = useI18n();
const router = useRouter();
const data = ref(null);
const isLoading = ref(true);
const hasError = ref(false);

const fetchData = async () => {
  isLoading.value = true;
  hasError.value = false;
  try {
    const response = props.mine
      ? await dealReportsAPI.getMyStalled(props.params)
      : await dealReportsAPI.getStalled(props.params);
    data.value = response.data;
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

watch(() => props.params, fetchData, { immediate: true, deep: true });

const openDeal = dealId => {
  router.push({ name: 'deals_edit', params: { dealId } });
};
</script>

<template>
  <section class="border rounded-lg border-n-weak">
    <h2 class="flex items-center gap-1 p-3 font-medium text-n-slate-12">
      <span v-if="mine">{{ t('DASHBOARD.MEU_ESPACO.SECTIONS.STALLED') }}</span>
      <span v-else>{{ t('DASHBOARD.COMERCIAL.SECTIONS.STALLED') }}</span>
      <fluent-icon
        v-tooltip="t('DASHBOARD.COMERCIAL.TOOLTIPS.STALLED')"
        icon="info"
        size="12"
      />
    </h2>

    <p v-if="hasError" class="px-3 pb-3 text-sm text-n-ruby-9">
      {{ t('DASHBOARD.COMERCIAL.LOAD_ERROR') }}
      <button type="button" class="underline" @click="fetchData">
        {{ t('DASHBOARD.COMERCIAL.RETRY') }}
      </button>
    </p>
    <p v-else-if="isLoading" class="px-3 pb-3 text-sm text-n-slate-11">
      {{ t('DASHBOARD.COMERCIAL.LOADING') }}
    </p>
    <div v-else class="px-3 pb-3">
      <p class="mb-2 text-sm text-n-slate-11">
        {{
          t('DASHBOARD.COMERCIAL.STALLED.SUMMARY', {
            count: data.total_count,
            value: formatCurrencyBRL(data.total_value),
          })
        }}
      </p>
      <p v-if="!data.rows.length" class="py-2 text-sm text-n-slate-10">
        {{ t('DASHBOARD.COMERCIAL.EMPTY_STATE') }}
      </p>
      <div v-else class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-n-slate-10">
              <th class="py-1 pr-3">
                <TableHeaderCell
                  :label="t('DASHBOARD.COMERCIAL.STALLED.NAME')"
                />
              </th>
              <th class="py-1 pr-3">
                <TableHeaderCell
                  :label="t('DASHBOARD.COMERCIAL.STALLED.PIPELINE')"
                />
              </th>
              <th class="py-1 pr-3">
                <TableHeaderCell
                  :label="t('DASHBOARD.COMERCIAL.STALLED.STAGE')"
                />
              </th>
              <th class="py-1 pr-3">
                <TableHeaderCell
                  :label="t('DASHBOARD.COMERCIAL.STALLED.VALUE')"
                />
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="deal in data.rows"
              :key="deal.id"
              class="border-t cursor-pointer border-n-weak hover:bg-n-alpha-1"
              @click="openDeal(deal.id)"
            >
              <td class="py-1 pr-3 text-n-slate-12">{{ deal.name }}</td>
              <td class="py-1 pr-3">{{ deal.pipeline_name }}</td>
              <td class="py-1 pr-3">{{ deal.stage_name }}</td>
              <td class="py-1 pr-3">{{ formatCurrencyBRL(deal.value) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>
